import { useCallback, useMemo, useRef } from 'react';
import {
    Stack,
    IPersonaProps,
    Text,
    mergeStyleSets,
    CompactPeoplePicker,
    IBasePickerSuggestionsProps,
    mergeStyles,
    Persona,
    PersonaSize,
    PersonaPresence,
    IconButton,
    Icon,
} from '@fluentui/react';
import { TimeUser } from '../../../../../api/generated/data-contracts';
import { useMutation, useQuery } from '@tanstack/react-query';
import { DELEGATES_FOR_USER } from '../../../../../constants/queryKeys';
import { ApiCalls } from '../../../../../api/api';
import { useUiContext } from '../../../../../components/Contexts/UIContext/uiCtx';
import { useSetQueryData } from '../../../../../hooks/useSetQueryData';
import { useMemoQueryKey } from '../../../../../hooks/useMemoQueryKey';
import { useConfigValuesState } from '../../../../../hooks/useConfigValue';
import { mapUserToPersona } from '../../../../../helpers/mapUserToPersona';

const styles = mergeStyleSets({
    description: {
        fontSize: '14px',
        opacity: 0.8,
        marginBottom: '30px',
        fontWeight: 500,
    },
    sectionTitle: {
        fontSize: '14px',
        fontWeight: 600,
        marginTop: '16px',
        marginBottom: '8px',
    },
    peoplePickerContainer: {
        marginTop: '8px',
    },
    directApproverDescription: {
        fontSize: '12px',
        opacity: 0.5,
        marginTop: '4px',
        display: 'flex',
        gap: 4,
        alignItems: 'center',
    },
});

export const DelegateConfiguration = ({ user }: { user: TimeUser }) => {
    const {
        initialData: {
            aggregatedUser: { originalUser },
            configuration: { directApproverDelegationEnabled },
        },
        setConfigValueCache,
    } = useConfigValuesState();
    const { isDarkmode } = useUiContext();
    const suggestionProps = useMemo<IBasePickerSuggestionsProps>(
        () => ({
            suggestionsHeaderText: 'Suggested People',
            mostRecentlyUsedHeaderText: 'Suggested Contacts',
            noResultsFoundText: 'No results found',
            loadingText: 'Loading',
            showRemoveButtons: false,
            suggestionsAvailableAlertText: 'People Picker Suggestions available',
            suggestionsContainerAriaLabel: 'Suggested contacts',
            resultsMaximumNumber: resultsSuperMaxNumber,
            searchForMoreText: 'Get all results',
            searchingText: 'Fetching all results...',
            resultsFooterFull: () => {
                return (
                    <div style={{ textAlign: 'center', color: isDarkmode ? 'rgb(208, 208, 208)' : 'rgb(96, 94, 92)', fontSize: '12px', lineHeight: '30ox' }}>
                        Max results reached, please specify search
                    </div>
                );
            },
            resultsFooter: () => {
                return (
                    <div style={{ textAlign: 'center', color: isDarkmode ? 'rgb(208, 208, 208)' : 'rgb(96, 94, 92)', fontSize: '12px', lineHeight: '30ox' }}>
                        All results shown
                    </div>
                );
            },
        }),
        [isDarkmode],
    );

    const qKey = useMemoQueryKey(DELEGATES_FOR_USER, user.id);
    const setDelegatesForUser = useSetQueryData<TimeUser[]>(qKey);
    const { data: delegatesForUser } = useQuery({
        queryKey: qKey,
        queryFn: () => ApiCalls.getDelegatesForUser(user.id).then(res => res.data),
        placeholderData: useRef([] as TimeUser[]).current,
    });

    const delegatePersonas = useMemo(() => {
        return delegatesForUser.map(mapUserToPersona);
    }, [delegatesForUser]);

    const { mutateAsync, data: userResult } = useMutation((searchText: string) => ApiCalls.searchUsers({ searchText }).then(res => res.data));
    const onResolveSuggestions = useCallback(
        (searchText: string) => {
            if (searchText) {
                return mutateAsync(searchText).then(users =>
                    // Filter out user results that it already added
                    users.filter(user => !delegatesForUser.some(delegatedUser => delegatedUser.id === user.id)).map(mapUserToPersona),
                );
            }
            // Returning null makes the picker do nothing. This is undocumented but is in the source code
            return null;
        },
        [delegatesForUser, mutateAsync],
    );

    const onAddDelegate = useCallback(
        (persona: IPersonaProps) => {
            const delegatedUser = userResult.find(user => user.id === persona.itemID);
            setDelegatesForUser(delegates => {
                if (delegates) {
                    return [delegatedUser, ...delegates];
                }
            });
            if (user.id === originalUser.id) {
                setConfigValueCache(initData => {
                    return {
                        ...initData,
                        aggregatedUser: {
                            ...initData.aggregatedUser,
                            delegatedUsers: [delegatedUser, ...initData.aggregatedUser.delegatedUsers],
                        },
                    };
                });
            }
            ApiCalls.setDelegates({ userId: user.id, delegateIds: [delegatedUser.id, ...delegatesForUser.map(user => user.id)] });
        },
        [delegatesForUser, originalUser.id, setConfigValueCache, setDelegatesForUser, user.id, userResult],
    );

    const onRemoveDelegate = useCallback(
        (persona: IPersonaProps) => {
            setDelegatesForUser(delegates => {
                if (delegates) {
                    return delegates.filter(user => user.id !== persona.itemID);
                }
            });
            if (user.id === originalUser.id) {
                setConfigValueCache(initData => {
                    return {
                        ...initData,
                        aggregatedUser: {
                            ...initData.aggregatedUser,
                            delegatedUsers: initData.aggregatedUser.delegatedUsers.filter(user => user.id !== persona.id),
                        },
                    };
                });
            }
            ApiCalls.setDelegates({ userId: user.id, delegateIds: delegatesForUser.filter(user => user.id !== persona.itemID).map(user => user.id) });
        },
        [delegatesForUser, originalUser.id, setConfigValueCache, setDelegatesForUser, user.id],
    );

    return (
        <>
            <Stack>
                {/* <Text className={styles.description}>Add or remove users that {user.name} can work as</Text> */}
                <Text className={styles.description}>Add or remove users that this user can work as</Text>
            </Stack>

            <Stack>
                <Text className={styles.sectionTitle}>Delegated users</Text>
                <Stack className={styles.peoplePickerContainer}>
                    <CompactPeoplePicker
                        styles={{ root: { maxWidth: 600, width: '100%' } }}
                        onResolveSuggestions={onResolveSuggestions}
                        itemLimit={10}
                        getTextFromItem={getTextFromItem}
                        pickerSuggestionsProps={suggestionProps}
                        className={'ms-PeoplePicker'}
                        inputProps={{
                            'aria-label': 'People Picker',
                            placeholder: ' Search here to select a user...',
                        }}
                        resolveDelay={500}
                        disabled={false}
                        onItemSelected={persona => {
                            if (persona) {
                                onAddDelegate(persona);
                                return null; // Clear the input after selection
                            }
                            return persona;
                        }}
                    />
                    {directApproverDelegationEnabled && (
                        <Text className={styles.directApproverDescription}>
                            <Icon iconName="Info" /> The user's employees are automatically available for delegation and don't need to be added to the list.
                        </Text>
                    )}
                    {delegatePersonas.length > 0 && (
                        <Stack className={delegateListStyles}>
                            {delegatePersonas.map((persona, index) => (
                                <Stack horizontal className={delegateItemStyles} key={`${persona.itemID}-${index}`}>
                                    <Persona
                                        imageInitials={persona.imageInitials}
                                        text={persona.text}
                                        secondaryText={persona.secondaryText}
                                        size={PersonaSize.size32}
                                        presence={PersonaPresence.none}
                                    />
                                    <IconButton
                                        iconProps={{ iconName: 'Cancel' }}
                                        title="Remove delegate"
                                        ariaLabel="Remove delegate"
                                        onClick={() => onRemoveDelegate(persona)}
                                    />
                                </Stack>
                            ))}
                        </Stack>
                    )}
                </Stack>
            </Stack>
        </>
    );
};

const resultsSuperMaxNumber: number = 50;

function getTextFromItem(persona: IPersonaProps): string {
    return persona.text;
}

const delegateListStyles = mergeStyles({
    marginTop: '16px',
    border: '1px solid #edebe9',
    borderRadius: '2px',
    padding: '8px',
    maxHeight: '200px',
    overflowY: 'auto',
});

const delegateItemStyles = mergeStyles({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px',
    margin: '4px 0',
    borderRadius: '2px',
});
